import type { Models } from '@smartlinkdev/types';
import { format, isWithinInterval, parseISO } from 'date-fns';
import _ from 'lodash';
import { multiLingualEnabled } from '$lib/stores/languages';
import { getTranslation } from '$lib/translations';
import { sanitizePossibleHTMLContent } from '$lib/utils';

export interface ExtHoursEntry {
    open_time: string;
    close_time: string;
    day_of_week: number;
    open_time_formatted: string;
    close_time_formatted: string;
}

/**
 * Formats a location URL to ensure it has the proper http:// prefix
 */
export function formatLocationUrl(url: string | undefined): string {
    if (!url) return '';
    
    if (url && !url.includes('http')) {
        return `http://${url}`;
    }
    
    return url;
}

/**
 * Determines if a location should display seasonal information
 */
export function displaySeasonal(location: Models.Location, venueIsSeasonal: boolean): boolean {
    try {
        if (location.seasonal_dates === null || location.seasonal_dates === undefined) {
            return false;
        }
        return venueIsSeasonal && Object.keys(location.seasonal_dates).length > 0;
    } catch (e) {
        return false;
    }
}

/**
 * Checks if the current date is within the location's seasonal dates
 */
export function withinSeasonalDates(location: Models.Location): boolean {
    if (!location.seasonal_dates) return false;
    
    const yearStart = new Date().getFullYear();
    let yearEnd = yearStart;

    if (location.seasonal_dates.start.month > location.seasonal_dates.end.month) {
        yearEnd++; // If it's a year rollover, adjust the end date's year
    }

    const start = `${yearStart}-${location.seasonal_dates.start.month}-${location.seasonal_dates.start.day}`;
    const end = `${yearEnd}-${location.seasonal_dates.end.month}-${location.seasonal_dates.end.day}`;

    const dates = [parseISO(start), parseISO(end)];
    return isWithinInterval(new Date(), {start: dates[0], end: dates[1]});
}

/**
 * Processes the location's hours data, handling both seasonal and regular hours
 */
export function processLocationHours(location: Models.Location, venueSeasonalEnabled: boolean): { 
    groupedHours: Record<string, ExtHoursEntry[]> | undefined, 
    isSeasonal: boolean 
} {
    let mondayToSundayHours: ExtHoursEntry[] = [];
    let isSeasonal = false;
    
    const locSeasonal = displaySeasonal(location, venueSeasonalEnabled);
    if (locSeasonal) {
        isSeasonal = withinSeasonalDates(location);
    }

    if (isSeasonal && location.seasonal_hours_entries) {
        mondayToSundayHours = location.seasonal_hours_entries.map((hour_entry: ExtHoursEntry): ExtHoursEntry => {
            return {
                open_time: hour_entry.open_time,
                close_time: hour_entry.close_time,
                day_of_week: (hour_entry.day_of_week === null) ? 7 : hour_entry.day_of_week,
                open_time_formatted: format(new Date(`2022-01-01 ${hour_entry.open_time}`), 'h:mm aaa'),
                close_time_formatted: format(new Date(`2022-01-01 ${hour_entry.close_time}`), 'h:mm aaa')
            }
        });
    } else if (!isSeasonal && location.hours_entries && location.hours_entries.length > 0) {
        mondayToSundayHours = location.hours_entries.map((hour_entry: ExtHoursEntry): ExtHoursEntry => {
            return {
                open_time: hour_entry.open_time,
                close_time: hour_entry.close_time,
                day_of_week: (hour_entry.day_of_week === null) ? 7 : hour_entry.day_of_week,
                open_time_formatted: format(new Date(`2022-01-01 ${hour_entry.open_time}`), 'h:mm aaa'),
                close_time_formatted: format(new Date(`2022-01-01 ${hour_entry.close_time}`), 'h:mm aaa')
            }
        });
    }

    let groupedHours: Record<string, ExtHoursEntry[]> | undefined;
    if (mondayToSundayHours.length > 0) {
        const sorted: ExtHoursEntry[] = _.sortBy(mondayToSundayHours, 'day_of_week', 'open_time');
        groupedHours = _.groupBy(sorted, 'day_of_week');
    }
    return { groupedHours, isSeasonal };
}

/**
 * Processes a location's description, handling translations if needed
 */
export function processDescription(location: Models.Location, currentLang?: string): string {
    if (!location || !location.description) return '';
    
    let description = location.description || '';
    
    if (multiLingualEnabled) {
        const found = getTranslation({
            modelTable: 'locations',
            modelAttribute: 'description',
            modelId: location.id,
        });
        
        if (found && found.text) {
            description = found.text;
        }
    }

    return sanitizePossibleHTMLContent(description);
}

/**
 * Calculates the appropriate fullSize for the details panel based on the location's attributes
 */
export function calculateFullSize(location: Models.Location): number {
    const highlightCategories = location.location_types?.filter((c: Models.Category) => c.highlight).length || 0;
    return 350 + (highlightCategories * 35);
}